/**
 * LandingPage — the unauthenticated-user home screen.
 *
 * Rendered by App.tsx when there is no signed-in user. Pure presentational +
 * a signup form that POSTs to /api/signup. No external state dependencies
 * beyond React and the props (onSignIn, onEmailSignIn, onPasswordReset,
 * authError).
 *
 * Extracted verbatim from App.tsx during M2 (PR 1 of the component breakup).
 */
import React, { useState } from 'react'
export default function LandingPage({ onSignIn, onEmailSignIn, onPasswordReset, authError }) {
  const teal = '#4a8fa8'
  const tealDark = '#3a7a94'
  const tealLight = '#e8f4f8'
  const tealBg = '#f0f8fb'

  const [showSignup, setShowSignup] = useState(false)

  // Email sign-in expansion state. Errors surface through the shared
  // authError prop (App.tsx maps Firebase codes to friendly strings);
  // resetNotice is local-only feedback for the forgot/set-password link.
  const [showEmailForm, setShowEmailForm] = useState(false)
  const [emailForm, setEmailForm] = useState({ email: '', password: '' })
  const [emailSubmitting, setEmailSubmitting] = useState(false)
  const [resetNotice, setResetNotice] = useState(null) // { type: 'success'|'info', message }

  const handleEmailSubmit = async (e) => {
    e.preventDefault()
    setResetNotice(null)
    setEmailSubmitting(true)
    try {
      await onEmailSignIn(emailForm.email, emailForm.password)
    } finally {
      setEmailSubmitting(false)
    }
  }

  const handleForgotPassword = async () => {
    setResetNotice(null)
    if (!emailForm.email.trim()) {
      setResetNotice({
        type: 'info',
        message: 'Enter your email above first, then click "Forgot password?"',
      })
      return
    }
    const sent = await onPasswordReset(emailForm.email)
    if (sent) {
      setResetNotice({
        type: 'success',
        message: `If an account exists for ${emailForm.email.trim()}, we've sent a password link. Check spam if you don't see it.`,
      })
    }
  }
  const [signupForm, setSignupForm] = useState({ name: '', email: '', stake: '', ward: '' })
  const [signupSubmitting, setSignupSubmitting] = useState(false)
  const [signupResult, setSignupResult] = useState(null) // { type: 'success'|'error', message }

  const handleSignupSubmit = async (e) => {
    e.preventDefault()
    setSignupSubmitting(true)
    setSignupResult(null)
    try {
      const res = await fetch('/api/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(signupForm),
      })
      const data = await res.json()
      if (res.ok) {
        setSignupResult({ type: 'success', message: "Thanks! We'll be in touch soon." })
        setSignupForm({ name: '', email: '', stake: '', ward: '' })
      } else {
        setSignupResult({ type: 'error', message: data.error || 'Something went wrong.' })
      }
    } catch (_err) {
      setSignupResult({ type: 'error', message: 'Network error. Please try again.' })
    } finally {
      setSignupSubmitting(false)
    }
  }

  const features = [
    {
      title: 'Sacrament Meetings',
      desc: 'Plan agendas, assign speakers and hymns, and share formatted programs.',
      icon: React.createElement('ion-icon', {
        name: 'book-outline',
        style: { fontSize: 28, color: 'currentColor' },
      }),
    },
    {
      title: 'Calling Management',
      desc: 'Track callings through every stage — from consideration to setting apart.',
      icon: React.createElement('ion-icon', {
        name: 'people-outline',
        style: { fontSize: 28, color: 'currentColor' },
      }),
    },
    {
      title: 'Interview Scheduling',
      desc: 'Schedule and track interviews across the bishopric with calendar views.',
      icon: React.createElement('ion-icon', {
        name: 'calendar-outline',
        style: { fontSize: 28, color: 'currentColor' },
      }),
    },
    {
      title: 'Meeting Agendas',
      desc: 'Create structured agendas for bishopric, PEC, and ward council meetings.',
      icon: React.createElement('ion-icon', {
        name: 'list-outline',
        style: { fontSize: 28, color: 'currentColor' },
      }),
    },
    {
      title: 'Member Directory',
      desc: 'Quick access to member information and contact details for your ward.',
      icon: React.createElement('ion-icon', {
        name: 'people-circle-outline',
        style: { fontSize: 28, color: 'currentColor' },
      }),
    },
    {
      title: 'Sacrament Tracking',
      desc: 'Monitor sacrament meeting attendance and participation trends over time.',
      icon: React.createElement('ion-icon', {
        name: 'bar-chart-outline',
        style: { fontSize: 28, color: 'currentColor' },
      }),
    },
  ]

  const googleLogoSvg = React.createElement(
    'svg',
    { width: '20', height: '20', viewBox: '0 0 24 24' },
    React.createElement('path', {
      fill: '#4285F4',
      d: 'M23.52 12.28c0-.98-.12-1.7-.3-2.46H12v4.46h6.6c-.18 1.16-.9 2.78-2.45 3.86v2.94h3.8c2.27-2.08 3.57-5.16 3.57-8.8z',
    }),
    React.createElement('path', {
      fill: '#34A853',
      d: 'M16.16 18.14c-1-.66-2.36-1.2-4.16-1.2-3.17 0-5.86-2.08-6.82-4.98H1.28v3.02C3.26 19.26 7.31 22 12 22c3.24 0 5.96-1.06 7.94-2.9l-3.78-2.96z',
    }),
    React.createElement('path', {
      fill: '#FBBC05',
      d: 'M5.18 14.28c-.24-.74-.38-1.54-.38-2.36s.14-1.62.38-2.36V6.54H1.28A11.95 11.95 0 000 12c0 1.94.46 3.78 1.28 5.4l3.9-3.12z',
    }),
    React.createElement('path', {
      fill: '#EA4335',
      d: 'M12 4.66c2.26 0 3.76.98 4.62 1.78l3.4-3.32C17.96 1.18 15.24 0 12 0 7.31 0 3.26 2.74 1.28 6.66l3.9 3.12C6.14 6.74 8.83 4.66 12 4.66z',
    }),
  )

  return (
    <div
      style={{
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
        color: '#1a2a36',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Nav */}
      <nav
        style={{
          padding: '16px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          maxWidth: '1080px',
          width: '100%',
          margin: '0 auto',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div
            style={{
              width: '32px',
              height: '32px',
              background: teal,
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ion-icon name="business-outline" style={{ fontSize: 18, color: 'white' }}></ion-icon>
          </div>
          <span style={{ fontWeight: 700, fontSize: '18px', color: '#1a2a36' }}>Wardflow</span>
        </div>
        <a
          href="#signin"
          style={{ color: teal, fontWeight: 600, fontSize: '14px', textDecoration: 'none' }}
        >
          Sign In
        </a>
      </nav>

      {/* Hero */}
      <section
        style={{
          padding: '60px 24px 48px',
          textAlign: 'center',
          background: `linear-gradient(180deg, ${tealBg} 0%, white 100%)`,
        }}
      >
        <div style={{ maxWidth: '640px', margin: '0 auto' }}>
          <h1
            style={{
              fontSize: 'clamp(28px, 5vw, 44px)',
              fontWeight: 800,
              lineHeight: 1.15,
              color: '#1a2a36',
              marginBottom: '16px',
            }}
          >
            Streamline your bishopric's workflow
          </h1>
          <p
            style={{
              fontSize: '18px',
              lineHeight: 1.6,
              color: '#5c6f85',
              marginBottom: '32px',
              maxWidth: '500px',
              margin: '0 auto 32px',
            }}
          >
            Plan sacrament meetings, manage callings, schedule interviews, and keep your ward
            organized — all in one place.
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a
              href="#signin"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                background: teal,
                color: 'white',
                fontWeight: 600,
                fontSize: '16px',
                padding: '14px 32px',
                borderRadius: '12px',
                textDecoration: 'none',
                transition: 'background 0.2s',
              }}
              onMouseOver={(e) => (e.currentTarget.style.background = tealDark)}
              onMouseOut={(e) => (e.currentTarget.style.background = teal)}
            >
              Get Started
              <ion-icon
                name="arrow-forward-outline"
                style={{ fontSize: 16, color: 'currentColor' }}
              ></ion-icon>
            </a>
            <button
              onClick={() => {
                setShowSignup(true)
                setSignupResult(null)
              }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                background: 'white',
                color: teal,
                fontWeight: 600,
                fontSize: '16px',
                padding: '14px 32px',
                borderRadius: '12px',
                border: `2px solid ${teal}`,
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = tealLight
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = 'white'
              }}
            >
              Sign Up
            </button>
          </div>
        </div>
      </section>

      {/* Features */}
      <section
        style={{ padding: '48px 24px 64px', maxWidth: '1080px', width: '100%', margin: '0 auto' }}
      >
        <h2
          style={{
            textAlign: 'center',
            fontSize: '24px',
            fontWeight: 700,
            color: '#1a2a36',
            marginBottom: '12px',
          }}
        >
          Everything your bishopric needs
        </h2>
        <p
          style={{ textAlign: 'center', fontSize: '16px', color: '#5c6f85', marginBottom: '40px' }}
        >
          Simple, focused tools built for ward leadership.
        </p>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '24px',
          }}
        >
          {features.map((f, i) => (
            <div
              key={i}
              style={{
                background: 'white',
                border: '1px solid #e8eff3',
                borderRadius: '16px',
                padding: '28px',
                transition: 'box-shadow 0.2s',
              }}
              onMouseOver={(e) =>
                (e.currentTarget.style.boxShadow = '0 4px 20px rgba(74,143,168,0.1)')
              }
              onMouseOut={(e) => (e.currentTarget.style.boxShadow = 'none')}
            >
              <div
                style={{
                  width: '48px',
                  height: '48px',
                  background: tealLight,
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: teal,
                  marginBottom: '16px',
                }}
              >
                {f.icon}
              </div>
              <h3
                style={{ fontSize: '17px', fontWeight: 650, color: '#1a2a36', marginBottom: '8px' }}
              >
                {f.title}
              </h3>
              <p style={{ fontSize: '14px', lineHeight: 1.6, color: '#5c6f85', margin: 0 }}>
                {f.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Sign In */}
      <section id="signin" style={{ padding: '64px 24px', background: tealBg }}>
        <div style={{ maxWidth: '420px', margin: '0 auto', textAlign: 'center' }}>
          <div
            style={{
              background: 'white',
              borderRadius: '20px',
              padding: '40px 32px',
              boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
            }}
          >
            <div
              style={{
                width: '56px',
                height: '56px',
                background: tealLight,
                borderRadius: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
                color: teal,
              }}
            >
              <ion-icon
                name="business-outline"
                style={{ fontSize: 28, color: 'currentColor' }}
              ></ion-icon>
            </div>
            <h2
              style={{ fontSize: '22px', fontWeight: 700, color: '#1a2a36', marginBottom: '8px' }}
            >
              Sign in to your ward
            </h2>
            <p style={{ fontSize: '14px', color: '#5c6f85', marginBottom: '24px' }}>
              Access your ward's dashboard with your Google account or email and password.
            </p>
            {authError && (
              <div
                style={{
                  marginBottom: '20px',
                  padding: '12px 16px',
                  background: '#fef2f2',
                  border: '1px solid #fee2e2',
                  borderRadius: '12px',
                }}
              >
                <p style={{ color: '#dc2626', fontSize: '13px', margin: 0 }}>{authError}</p>
              </div>
            )}
            <button
              onClick={onSignIn}
              style={{
                width: '100%',
                background: 'white',
                border: '2px solid #e2e8f0',
                borderRadius: '12px',
                padding: '14px 24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                cursor: 'pointer',
                fontSize: '15px',
                fontWeight: 600,
                color: '#334155',
                transition: 'all 0.2s',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.borderColor = '#cbd5e1'
                e.currentTarget.style.background = '#f8fafc'
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.borderColor = '#e2e8f0'
                e.currentTarget.style.background = 'white'
              }}
            >
              {googleLogoSvg}
              Continue with Google
            </button>

            {/* Divider */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                margin: '16px 0',
              }}
            >
              <div style={{ flex: 1, height: '1px', background: '#e2e8f0' }} />
              <span style={{ fontSize: '12px', color: '#94a3b8' }}>or</span>
              <div style={{ flex: 1, height: '1px', background: '#e2e8f0' }} />
            </div>

            {!showEmailForm ? (
              <button
                onClick={() => setShowEmailForm(true)}
                style={{
                  width: '100%',
                  background: 'white',
                  border: '2px solid #e2e8f0',
                  borderRadius: '12px',
                  padding: '14px 24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px',
                  cursor: 'pointer',
                  fontSize: '15px',
                  fontWeight: 600,
                  color: '#334155',
                  transition: 'all 0.2s',
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.borderColor = '#cbd5e1'
                  e.currentTarget.style.background = '#f8fafc'
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.borderColor = '#e2e8f0'
                  e.currentTarget.style.background = 'white'
                }}
              >
                <ion-icon name="mail-outline" style={{ fontSize: 20, color: '#334155' }}></ion-icon>
                Sign in with Email
              </button>
            ) : (
              <form onSubmit={handleEmailSubmit} style={{ textAlign: 'left' }}>
                <input
                  type="email"
                  value={emailForm.email}
                  onChange={(e) => setEmailForm({ ...emailForm, email: e.target.value })}
                  placeholder="Email"
                  autoComplete="email"
                  autoFocus
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    border: '2px solid #e2e8f0',
                    borderRadius: '12px',
                    padding: '12px 16px',
                    fontSize: '15px',
                    color: '#334155',
                    marginBottom: '10px',
                    outline: 'none',
                  }}
                />
                <input
                  type="password"
                  value={emailForm.password}
                  onChange={(e) => setEmailForm({ ...emailForm, password: e.target.value })}
                  placeholder="Password"
                  autoComplete="current-password"
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    border: '2px solid #e2e8f0',
                    borderRadius: '12px',
                    padding: '12px 16px',
                    fontSize: '15px',
                    color: '#334155',
                    marginBottom: '12px',
                    outline: 'none',
                  }}
                />
                {resetNotice && (
                  <div
                    style={{
                      marginBottom: '12px',
                      padding: '10px 14px',
                      background: resetNotice.type === 'success' ? '#f0fdf4' : '#f8fafc',
                      border: `1px solid ${resetNotice.type === 'success' ? '#bbf7d0' : '#e2e8f0'}`,
                      borderRadius: '12px',
                    }}
                  >
                    <p
                      style={{
                        color: resetNotice.type === 'success' ? '#15803d' : '#5c6f85',
                        fontSize: '13px',
                        margin: 0,
                      }}
                    >
                      {resetNotice.message}
                    </p>
                  </div>
                )}
                <button
                  type="submit"
                  disabled={emailSubmitting || !emailForm.email.trim() || !emailForm.password}
                  style={{
                    width: '100%',
                    background: emailSubmitting ? '#7fb0c4' : teal,
                    border: 'none',
                    borderRadius: '12px',
                    padding: '14px 24px',
                    fontSize: '15px',
                    fontWeight: 600,
                    color: 'white',
                    cursor: emailSubmitting ? 'default' : 'pointer',
                  }}
                >
                  {emailSubmitting ? 'Signing in…' : 'Sign in'}
                </button>
                <p style={{ fontSize: '13px', color: '#5c6f85', marginTop: '12px', marginBottom: 0 }}>
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault()
                      handleForgotPassword()
                    }}
                    style={{ color: teal, fontWeight: 600, textDecoration: 'none' }}
                  >
                    Forgot password?
                  </a>{' '}
                  First time signing in? Use this to set your password.
                </p>
              </form>
            )}

            <p style={{ fontSize: '12px', color: '#94a3b8', marginTop: '16px' }}>
              You must be invited by a ward administrator to access your ward.
            </p>
          </div>
          <p style={{ fontSize: '13px', color: '#5c6f85', marginTop: '20px' }}>
            Stake administrator?{' '}
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault()
                onSignIn()
              }}
              style={{ color: teal, fontWeight: 600, textDecoration: 'none' }}
            >
              Sign in here
            </a>
          </p>
        </div>
      </section>

      {/* Sign Up Modal */}
      {showSignup && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
          }}
          onClick={() => setShowSignup(false)}
        >
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(0,0,0,0.4)',
              backdropFilter: 'blur(4px)',
            }}
          />
          <div
            style={{
              position: 'relative',
              background: 'white',
              borderRadius: '20px',
              padding: '36px 32px',
              maxWidth: '440px',
              width: '100%',
              boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowSignup(false)}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#94a3b8',
                fontSize: '20px',
                lineHeight: 1,
              }}
            >
              ✕
            </button>
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <div
                style={{
                  width: '56px',
                  height: '56px',
                  background: tealLight,
                  borderRadius: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px',
                  color: teal,
                }}
              >
                <ion-icon
                  name="business-outline"
                  style={{ fontSize: 28, color: 'currentColor' }}
                ></ion-icon>
              </div>
              <h2
                style={{ fontSize: '22px', fontWeight: 700, color: '#1a2a36', marginBottom: '8px' }}
              >
                Sign up for Wardflow
              </h2>
              <p style={{ fontSize: '14px', color: '#5c6f85', margin: 0 }}>
                Tell us about your ward and we'll get you set up.
              </p>
            </div>
            {signupResult?.type === 'success' ? (
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <div
                  style={{
                    width: '48px',
                    height: '48px',
                    background: '#ecfdf5',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 16px',
                    color: '#059669',
                    fontSize: '24px',
                  }}
                >
                  ✓
                </div>
                <p
                  style={{
                    fontSize: '16px',
                    fontWeight: 600,
                    color: '#1a2a36',
                    marginBottom: '8px',
                  }}
                >
                  {signupResult.message}
                </p>
                <button
                  onClick={() => setShowSignup(false)}
                  style={{
                    marginTop: '12px',
                    background: teal,
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    padding: '12px 28px',
                    fontWeight: 600,
                    fontSize: '14px',
                    cursor: 'pointer',
                  }}
                >
                  Done
                </button>
              </div>
            ) : (
              <form onSubmit={handleSignupSubmit}>
                {signupResult?.type === 'error' && (
                  <div
                    style={{
                      marginBottom: '16px',
                      padding: '12px 16px',
                      background: '#fef2f2',
                      border: '1px solid #fee2e2',
                      borderRadius: '12px',
                    }}
                  >
                    <p style={{ color: '#dc2626', fontSize: '13px', margin: 0 }}>
                      {signupResult.message}
                    </p>
                  </div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  <div>
                    <label
                      style={{
                        display: 'block',
                        fontSize: '13px',
                        fontWeight: 600,
                        color: '#475569',
                        marginBottom: '6px',
                      }}
                    >
                      Name
                    </label>
                    <input
                      type="text"
                      required
                      value={signupForm.name}
                      onChange={(e) => setSignupForm((f) => ({ ...f, name: e.target.value }))}
                      placeholder="Your full name"
                      style={{
                        width: '100%',
                        padding: '12px 14px',
                        background: '#f8fafc',
                        border: '1.5px solid #e2e8f0',
                        borderRadius: '10px',
                        fontSize: '15px',
                        outline: 'none',
                        boxSizing: 'border-box',
                        transition: 'border-color 0.2s',
                      }}
                      onFocus={(e) => (e.target.style.borderColor = teal)}
                      onBlur={(e) => (e.target.style.borderColor = '#e2e8f0')}
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        display: 'block',
                        fontSize: '13px',
                        fontWeight: 600,
                        color: '#475569',
                        marginBottom: '6px',
                      }}
                    >
                      Email
                    </label>
                    <input
                      type="email"
                      required
                      value={signupForm.email}
                      onChange={(e) => setSignupForm((f) => ({ ...f, email: e.target.value }))}
                      placeholder="you@example.com"
                      style={{
                        width: '100%',
                        padding: '12px 14px',
                        background: '#f8fafc',
                        border: '1.5px solid #e2e8f0',
                        borderRadius: '10px',
                        fontSize: '15px',
                        outline: 'none',
                        boxSizing: 'border-box',
                        transition: 'border-color 0.2s',
                      }}
                      onFocus={(e) => (e.target.style.borderColor = teal)}
                      onBlur={(e) => (e.target.style.borderColor = '#e2e8f0')}
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        display: 'block',
                        fontSize: '13px',
                        fontWeight: 600,
                        color: '#475569',
                        marginBottom: '6px',
                      }}
                    >
                      Stake
                    </label>
                    <input
                      type="text"
                      required
                      value={signupForm.stake}
                      onChange={(e) => setSignupForm((f) => ({ ...f, stake: e.target.value }))}
                      placeholder="e.g. Driggs Idaho Stake"
                      style={{
                        width: '100%',
                        padding: '12px 14px',
                        background: '#f8fafc',
                        border: '1.5px solid #e2e8f0',
                        borderRadius: '10px',
                        fontSize: '15px',
                        outline: 'none',
                        boxSizing: 'border-box',
                        transition: 'border-color 0.2s',
                      }}
                      onFocus={(e) => (e.target.style.borderColor = teal)}
                      onBlur={(e) => (e.target.style.borderColor = '#e2e8f0')}
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        display: 'block',
                        fontSize: '13px',
                        fontWeight: 600,
                        color: '#475569',
                        marginBottom: '6px',
                      }}
                    >
                      Ward
                    </label>
                    <input
                      type="text"
                      required
                      value={signupForm.ward}
                      onChange={(e) => setSignupForm((f) => ({ ...f, ward: e.target.value }))}
                      placeholder="e.g. Driggs 1st Ward"
                      style={{
                        width: '100%',
                        padding: '12px 14px',
                        background: '#f8fafc',
                        border: '1.5px solid #e2e8f0',
                        borderRadius: '10px',
                        fontSize: '15px',
                        outline: 'none',
                        boxSizing: 'border-box',
                        transition: 'border-color 0.2s',
                      }}
                      onFocus={(e) => (e.target.style.borderColor = teal)}
                      onBlur={(e) => (e.target.style.borderColor = '#e2e8f0')}
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={signupSubmitting}
                  style={{
                    width: '100%',
                    marginTop: '20px',
                    background: signupSubmitting ? '#94a3b8' : teal,
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    padding: '14px',
                    fontWeight: 600,
                    fontSize: '15px',
                    cursor: signupSubmitting ? 'default' : 'pointer',
                    transition: 'background 0.2s',
                  }}
                  onMouseOver={(e) => {
                    if (!signupSubmitting) e.currentTarget.style.background = tealDark
                  }}
                  onMouseOut={(e) => {
                    if (!signupSubmitting) e.currentTarget.style.background = teal
                  }}
                >
                  {signupSubmitting ? 'Submitting...' : 'Sign Up'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Footer */}
      <footer style={{ padding: '32px 24px', borderTop: '1px solid #e8eff3', marginTop: 'auto' }}>
        <div
          style={{
            maxWidth: '1080px',
            margin: '0 auto',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
          }}
        >
          <span style={{ fontSize: '13px', color: '#94a3b8' }}>© 2026 Wardflow</span>
          <div style={{ display: 'flex', gap: '24px' }}>
            <a
              href="/privacy.html"
              style={{
                fontSize: '13px',
                color: '#5c6f85',
                textDecoration: 'none',
                fontWeight: 500,
              }}
            >
              Privacy Policy
            </a>
            <a
              href="mailto:privacy@wardflow.org"
              style={{
                fontSize: '13px',
                color: '#5c6f85',
                textDecoration: 'none',
                fontWeight: 500,
              }}
            >
              Contact
            </a>
          </div>
        </div>
      </footer>
    </div>
  )
}
